import { useEffect, useState } from 'react';
import SearchBar from '../../Forms/SearchBar/SearchBar';
import BasicPagination from '../../DataTable/BasicPagination';
import DesktopTableInlineActions from '../../Tables/desktop/DesktopTableInlineActions';
import { useProductStockLookup } from '../../../hooks/inventory/useProductStockLookup';
import { ProductStockLookupDTO } from '../../../types/inventory/stock/ProductStockLookupTypes';

interface Props {
  branchId: number | null | undefined;
  onSelect: (product: ProductStockLookupDTO) => void;
  excludeProductIds?: number[];
}

const pickerColumns = [
  { accessor: 'productName', label: 'Producto' },
  { accessor: 'productCode', label: 'Código' },
  {
    accessor: 'stockQuantity',
    label: 'Stock',
    render: (_: any, row: any) => `${Number(row.stockQuantity).toFixed(2)} ${row.unitCode ?? ''}`,
  },
];

/**
 * Tabla paginada compartida para ESCOGER un producto con stock de una
 * sucursal — usada por Ajustes y Traslados al agregar un producto.
 * Sin término de búsqueda lista todo lo disponible, paginado.
 *
 * Reutiliza DesktopTableInlineActions + BasicPagination (los mismos
 * componentes del resto de la app) en vez de una tabla propia.
 */
const ProductStockPickerTable = ({ branchId, onSelect, excludeProductIds = [] }: Props) => {
  const { list, paginator, search, reset } = useProductStockLookup();
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);

  useEffect(() => {
    if (branchId) {
      search(branchId, term, page, size);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, term, page, size]);

  useEffect(() => {
    setPage(0);
  }, [term]);

  useEffect(() => {
    return () => {
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🛑 DesktopTableInlineBody usa row.id como key de React — sin este alias
  // todas las filas comparten key=undefined, lo que provoca reconciliación
  // incorrecta (filas "pegadas"/lentas) al buscar o cambiar de página.
  const rows = list
    .filter((p) => !excludeProductIds.includes(p.productId))
    .map((p) => ({ ...p, id: p.productId }));

  const pickerActions = (row: ProductStockLookupDTO) => [
    {
      label: 'Seleccionar',
      icon: 'check-circle',
      onClick: () => onSelect(row),
    },
  ];

  return (
    <div>
      <div className="mb-3">
        <SearchBar
          value={term}
          onChange={setTerm}
          placeholder="Buscar producto por nombre, código o SKU..."
          iconKey="search"
        />
      </div>

      <DesktopTableInlineActions
        columns={pickerColumns}
        data={rows}
        actions={pickerActions}
        noDataMessage="No hay productos disponibles."
      />

      {paginator && (
        <div className="mt-4">
          <BasicPagination
            currentPage={paginator.number ?? 0}
            totalPages={paginator.totalPages ?? 0}
            totalElements={paginator.totalElements}
            last={paginator.last}
            size={size}
            onPageChange={setPage}
            onSizeChange={(s) => {
              setSize(s);
              setPage(0);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ProductStockPickerTable;
